import { useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../../../redux/notification';
import { getShiftsToRequest } from '../../../../../hooks/useShiftsForm';
import {
	useSubmitBulkCreateAndAssignMutation,
	useGetCaregiverScheduleQuery
} from '../../../../../redux/bulkAssignShiftsApi';
import computeBulkAssignOverlaps, { getDateConflictForStructure } from '../computeBulkAssignOverlaps';

const MAX_BULK_DATES = 30;

const useBulkAssignSubmit = ({
	selectedFacility,
	selectedCaregiver,
	shiftsGroups,
	shiftStructures,
	isOnRequestTab,
	reset,
	needMoreShifts,
	handleBackendErrors,
	checkIntegrity,
	setIsLoading,
	updateFeed,
	onClose
}) => {
	const [showAlreadyStartedWarning, setShowAlreadyStartedWarning] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const dispatch = useDispatch();
	const [submitBulkCreateAndAssign] = useSubmitBulkCreateAndAssignMutation();

	const facilityId = selectedFacility?.id;
	const caregiverId = selectedCaregiver?.id;
	const facilityTimezone = selectedFacility?.timezone;

	const { data: caregiverSchedule = [] } = useGetCaregiverScheduleQuery(
		{ facilityId, caregiverId },
		{ skip: isOnRequestTab || !facilityId || !caregiverId }
	);

	const overlapErrors = useMemo(
		() => computeBulkAssignOverlaps({ caregiverSchedule, shiftsGroups, shiftStructures, facilityTimezone }),
		[caregiverSchedule, shiftsGroups, shiftStructures, facilityTimezone]
	);

	const unavailableDates = useMemo(() => {
		const flat = new Set();
		Object.values(overlapErrors).forEach((groupErrors) => {
			if (groupErrors.unavailableDates) {
				groupErrors.unavailableDates.forEach((isoDate) => flat.add(isoDate));
			}
		});
		return flat;
	}, [overlapErrors]);

	const getDateConflict = useCallback(
		(shiftStructureId, date) => {
			if (!shiftStructureId) {
				return null;
			}
			const structure = (shiftStructures || []).find((s) => s.id === shiftStructureId);
			return getDateConflictForStructure({
				caregiverSchedule,
				structure,
				date,
				facilityTimezone
			});
		},
		[caregiverSchedule, shiftStructures, facilityTimezone]
	);

	const hasBlockingOverlaps = useMemo(
		() =>
			Object.values(overlapErrors).some(
				(groupErrors) =>
					(groupErrors.overlappingDates && groupErrors.overlappingDates.size > 0) ||
					(groupErrors.internalOverlapDates && groupErrors.internalOverlapDates.size > 0)
			),
		[overlapErrors]
	);

	const closeAllConfirmations = () => {
		setShowAlreadyStartedWarning(false);
		setShowConfirmation(false);
	};

	const submit = () => {
		if (!selectedFacility) {
			return;
		}
		if (!selectedCaregiver) {
			dispatch(showNotification('error', 'Please select a professional to assign these shifts to.'));
			return;
		}
		const verdict = checkIntegrity(true);
		if (!verdict.isValid) {
			return;
		}
		const totalDates = shiftsGroups.reduce((total, group) => total + (group.dates ? group.dates.length : 0), 0);
		if (totalDates > MAX_BULK_DATES) {
			dispatch(showNotification('error', `You can only assign at most ${MAX_BULK_DATES} shifts per submission.`));
			return;
		}
		if (hasBlockingOverlaps) {
			dispatch(
				showNotification(
					'error',
					'Some selected dates conflict with the professional’s schedule. Please review the highlighted dates.'
				)
			);
			return;
		}
		if (verdict.hasFlexStructuresAlreadyStarted) {
			setShowAlreadyStartedWarning(true);
		} else {
			setShowConfirmation(true);
		}
	};

	const postBulkAssign = async () => {
		if (!selectedFacility || !selectedCaregiver) {
			return;
		}
		setIsSubmitting(true);
		setIsLoading(true);
		try {
			const shiftsData = getShiftsToRequest(shiftsGroups, true).map((shift) => ({
				structure_id: shift.structure_id,
				unit_id: shift.unit_id,
				start_date: shift.start_date
			}));
			await submitBulkCreateAndAssign({
				facilityId: selectedFacility.id,
				caregiverId: selectedCaregiver.id,
				shiftsData
			}).unwrap();
			dispatch(showNotification('success', 'Shifts requested and assigned successfully'));
			if (updateFeed) {
				updateFeed();
			}
			if (needMoreShifts) {
				reset();
			} else {
				onClose();
			}
		} catch (e) {
			const errorData = e && e.data;
			if (errorData && errorData.validationError) {
				handleBackendErrors(errorData.errors);
				const firstMessage = (errorData.errors && errorData.errors[0] && errorData.errors[0].message) || null;
				dispatch(showNotification('error', firstMessage || 'Could not assign shifts. Please review your entries.'));
			} else if (e && e.status === 403) {
				dispatch(showNotification('error', 'You do not have permission to assign shifts at this facility.'));
				onClose();
			} else {
				dispatch(showNotification('error', 'Failed to assign shifts. Please try again.'));
			}
		} finally {
			closeAllConfirmations();
			setIsSubmitting(false);
			setIsLoading(false);
		}
	};

	const advanceFromWarning = () => {
		setShowAlreadyStartedWarning(false);
		setShowConfirmation(true);
	};

	const variant = showAlreadyStartedWarning ? 'already-started-warning' : 'default';
	const onConfirm = showAlreadyStartedWarning ? advanceFromWarning : postBulkAssign;

	return {
		submit,
		confirmLabel: 'Add assigned shifts',
		submitDisabled: !selectedCaregiver,
		isSubmitting,
		overlapErrors,
		getDateConflict,
		confirmation: {
			isOpen: showAlreadyStartedWarning || showConfirmation,
			variant,
			unavailableDates,
			onConfirm,
			onCancel: closeAllConfirmations
		}
	};
};

export default useBulkAssignSubmit;
